import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useRealtime } from '@/hooks/useRealtime';
import { useAuth } from '@/hooks/useAuth';
import AuthGuard from '@/components/feature/AuthGuard';
import AdminLayout from '@/pages/dashboard/admin/components/AdminLayout';

interface SponsorCategory {
  id: string;
  name: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
}

export default function SponsorCategoriesPage() {
  const { profile } = useAuth();
  const [categories, setCategories] = useState<SponsorCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', display_order: 0, is_active: true });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error: err } = await supabase
        .from('sponsor_categories')
        .select('*')
        .order('display_order', { ascending: true });
      if (err) throw err;
      setCategories(data || []);
      setError('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  useRealtime<SponsorCategory>({ table: 'sponsor_categories', onAny: () => loadCategories() });

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Category name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editId) {
        const { error: err } = await supabase
          .from('sponsor_categories')
          .update({ name: form.name.trim(), display_order: form.display_order, is_active: form.is_active })
          .eq('id', editId);
        if (err) throw err;
      } else {
        const { error: err } = await supabase
          .from('sponsor_categories')
          .insert({
            name: form.name.trim(),
            display_order: form.display_order,
            is_active: form.is_active,
            created_by: profile?.id,
          });
        if (err) throw err;
        await supabase.from('audit_logs').insert({
          user_id: profile?.id, user_name: profile?.full_name || 'Admin', role: profile?.role,
          action: 'Sponsor Category Created', module: 'Sponsors',
        });
      }
      setShowModal(false);
      setEditId(null);
      setForm({ name: '', display_order: 0, is_active: true });
      setError('');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save category';
      setError(msg);
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error: err } = await supabase.from('sponsor_categories').delete().eq('id', id);
      if (err) throw err;
      await supabase.from('audit_logs').insert({
        user_id: profile?.id, user_name: profile?.full_name || 'Admin', role: profile?.role,
        action: 'Sponsor Category Deleted', module: 'Sponsors',
      });
      setDeleteConfirm(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete category');
      setDeleteConfirm(null);
    }
  };

  const openEdit = (c: SponsorCategory) => {
    setEditId(c.id);
    setForm({ name: c.name, display_order: c.display_order, is_active: c.is_active });
    setShowModal(true);
  };

  return (
    <AuthGuard requiredRole="admin">
      <AdminLayout title="Sponsor Categories" breadcrumbs={[{ label: 'Dashboard', href: '/dashboard/admin' }, { label: 'Sponsors' }, { label: 'Categories' }]}>
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm flex items-center justify-between">
            <div className="flex items-start gap-2">
              <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
              <span>{error}</span>
            </div>
            <button onClick={() => setError('')} className="cursor-pointer flex-shrink-0 ml-2"><i className="ri-close-line"></i></button>
          </div>
        )}

        <div className="flex justify-end mb-5">
          <button onClick={() => { setEditId(null); setForm({ name: '', display_order: categories.length, is_active: true }); setShowModal(true); setError(''); }} className="px-4 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
            <i className="ri-add-line"></i> Add Category
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div></div>
        ) : categories.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-price-tag-3-line text-2xl text-foreground-300"></i>
            </div>
            <p className="text-foreground-400 text-sm">No sponsor categories yet</p>
            <p className="text-foreground-300 text-xs mt-1">Click &quot;Add Category&quot; to create your first sponsor category</p>
          </div>
        ) : (
          <div className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200/70 bg-background-100/50">
                    {['Category Name', 'Display Order', 'Active', 'Created', 'Actions'].map((l) => (
                      <th key={l} className="px-4 py-3 text-left text-xs font-semibold text-foreground-500 whitespace-nowrap">{l}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {categories.map((c) => (
                    <tr key={c.id} className={`border-b border-background-100 hover:bg-background-50/50 transition-colors ${!c.is_active ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-3 font-medium text-foreground-900">{c.name}</td>
                      <td className="px-4 py-3 text-foreground-600 text-xs">{c.display_order}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${c.is_active ? 'bg-accent-100 text-accent-700' : 'bg-background-200 text-foreground-400'}`}>
                          {c.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-foreground-500 text-xs whitespace-nowrap">{new Date(c.created_at).toLocaleDateString('en-IN')}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEdit(c)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-accent-50 text-foreground-400 hover:text-accent-600 transition-colors cursor-pointer" title="Edit">
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                          <button onClick={() => setDeleteConfirm(c.id)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-foreground-400 hover:text-red-600 transition-colors cursor-pointer" title="Delete">
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Delete confirmation modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setDeleteConfirm(null)}>
            <div className="bg-background-50 rounded-2xl shadow-xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
              <div className="w-10 h-10 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-3">
                <i className="ri-error-warning-line text-lg text-red-600"></i>
              </div>
              <h3 className="font-heading text-base font-bold text-foreground-950 text-center mb-1">Delete Category?</h3>
              <p className="text-sm text-foreground-500 text-center mb-5">This will also affect sponsors assigned to this category.</p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteConfirm(null)} className="flex-1 px-4 py-2 rounded-lg border border-background-200 text-foreground-600 text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cancel</button>
                <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap">Delete</button>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowModal(false)}>
            <div className="bg-background-50 rounded-2xl shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <div className="border-b border-background-200/70 p-5 flex items-center justify-between">
                <h3 className="font-heading text-lg font-bold text-foreground-950">{editId ? 'Edit' : 'Add'} Category</h3>
                <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer">
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Category Name <span className="text-red-500">*</span></label>
                  <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 outline-none" placeholder="e.g. Platinum Sponsor" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Display Order</label>
                  <input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value) || 0 })} className="w-24 px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 outline-none" min="0" />
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4 rounded accent-primary-500" />
                    <span className="text-sm text-foreground-700">Active</span>
                  </label>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg border border-background-200 text-foreground-600 text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cancel</button>
                  <button onClick={handleSave} disabled={saving} className="px-5 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 disabled:cursor-not-allowed text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
                    {saving && <div className="w-4 h-4 rounded-full border-2 border-background-50 border-t-transparent animate-spin"></div>}
                    {saving ? 'Saving...' : editId ? 'Save Changes' : 'Add Category'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </AdminLayout>
    </AuthGuard>
  );
}